/**
 * Os blocos de uma lista que NÃO é feita das 32 métricas de envio: indicadores e gráfico acima da
 * lista, em Cliques e nas fontes de Leads.
 *
 * Por que não o `ScreenBlocks`: lá todo número sai do catálogo de métricas e do bloco de receita,
 * envio e taxa que a API manda em toda linha. Aqui a linha tem números próprios — cliques,
 * contatos, leads confirmados — e dois deles mudam tudo:
 *
 * 1. **contato único não soma entre linhas.** A mesma pessoa pode estar em dois links, e somar daria
 *    um número maior que a realidade. Medida que não soma fica fora da composição e do total, e o
 *    indicador dela é "—" com o motivo no title — avisar, não omitir;
 * 2. **não existe funil de entrega** sem contador de envio. O bloco é simplesmente ignorado aqui.
 *
 * O desenho é o mesmo das outras telas de propósito: a faixa de indicadores vem do `KpiRow` e as
 * barras do `chartbits`, as duas compartilhadas com as telas de métrica.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Settings2 } from 'lucide-react';
import { GhostBtn } from '@/components/v2/ui';
import { c, v, type Hue } from '@/components/v2/theme';
import { Block } from './Block';
import { HeatGrid, MixBar, Pills, RankBars, ScatterPlot, TOP_N, sliceUp, type Bar, type Dot, type HeatCol } from './chartbits';
import { ModelPicker } from './ModelPicker';
import { KpiRow, type KpiItem } from './overview/Kpis';
import type { DataReg } from '../lib/cards';
import { DASH } from '../lib/format';
import { SCREEN_BLOCK_META, type ScreenBlock, type ScreenPrefs } from '../lib/prefs';
import type { ScreenKey } from '../lib/screens';
import { useEmail } from '../state/store';

/** Uma linha desta lista, do jeito que os gráficos precisam dela. */
export interface DataRow<R> {
    key: string;
    label: string;
    row: R;
    /** Cor própria, quando a tela agrupa por algo que já tem cor. */
    hue?: Hue;
}

/** Um número que esta lista mede. */
export interface Measure<R> {
    k: string;
    l: string;
    d: string;
    hue?: Hue;
    /** O valor da linha; `null` = esta linha não tem esse número. */
    get: (row: R) => number | null;
    /** Como escrever o valor — cada tela tem o seu formatador. */
    text: (n: number | null) => string;
    /**
     * Soma entre linhas? Só o que soma entra na composição e na concentração: repartir uma taxa, ou
     * um contato que pode estar em duas linhas, não quer dizer nada.
     */
    additive?: boolean;
}

export interface DataBlocksProps<R> {
    screenKey: ScreenKey;
    items: DataRow<R>[];
    /** Como se chama uma linha desta lista, no plural: "links", "templates", "fontes". */
    what: string;
    /**
     * Os indicadores desta visão, já escritos: quem sabe o que soma é a tela.
     *
     * Vazio ou ausente quer dizer que esta visão NÃO tem indicadores próprios — as fontes de Leads
     * são assim: os quatro números do topo daquela tela são do período, inclusive as perdas, que
     * não têm fonte. O bloco então não existe, e o Personalizar não oferece a aba dele.
     */
    kpis?: KpiItem[];
    /** Os números que o gráfico oferece. */
    measures: Measure<R>[];
    /** A ressalva desta visão — o que estes números não dizem. */
    note?: ReactNode;
    /** A lista — desenhada na posição que o bloco `list` ocupa na ordem. */
    children: ReactNode;
}

/**
 * Os blocos que uma visão destas tem. A lista sempre; os indicadores, se ela os trouxer; o gráfico,
 * se ela tiver alguma medida — sem número para comparar não há o que desenhar, e um bloco vazio é
 * pior do que bloco nenhum.
 */
export const dataBlocks = (kpis?: KpiItem[], measures?: { k: string }[]): ScreenBlock[] => [
    ...(kpis?.length ? ['kpis' as const] : []),
    ...(measures?.length ? ['chart' as const] : []),
    'list' as const,
];

/** Os modelos de gráfico de uma lista destas. A composição e a concentração só com número que soma. */
export const dataModels = <R,>(measures: Measure<R>[], count: number) => {
    const sums = measures.some((m) => m.additive);
    return [
        { k: 'rank', l: 'Ranking', d: 'As maiores linhas na medida escolhida, em barras.' },
        ...(sums ? [{ k: 'mix', l: 'Composição', d: 'A fatia de cada linha no total somado da lista.' }] : []),
        ...(sums && count > 1 ? [{ k: 'conc', l: 'Concentração', d: 'Quanto do total está nas maiores linhas.' }] : []),
        ...(measures.length > 1 ? [{ k: 'pair', l: 'Medida × medida', d: 'Duas medidas à sua escolha; cada linha é um ponto.' }] : []),
        { k: 'heat', l: 'Mapa de calor', d: 'Medidas × linhas, verde acima e vermelho abaixo da média da lista.' },
    ];
};

/** O modelo que a lista realmente desenha: o salvo, quando existe aqui, e o ranking quando não. */
const pickModel = (models: { k: string }[], pref: string) =>
    models.some((m) => m.k === pref) ? pref : 'rank';

export function DataBlocks<R>(props: DataBlocksProps<R>) {
    const { prefs, registerBlocks } = useEmail();
    const p = prefs.screens[props.screenKey];
    const models = dataModels(props.measures, props.items.length);

    // sem array de dependências de propósito: só escreve uma ref, não dispara renderização.
    // `block` recebe as preferências como ARGUMENTO: assim o Personalizar desenha com as de agora,
    // e não com as que valiam quando este efeito rodou.
    useEffect(() => {
        const reg: DataReg = {
            kind: 'data',
            blocks: dataBlocks(props.kpis, props.measures),
            models,
            note: props.note,
            count: props.items.length,
            block: (b, live) => <Draw {...props} p={live} block={b} preview />,
        };
        registerBlocks(reg);
        return () => registerBlocks(null);
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {props.note && !!props.items.length && <DataNote>{props.note}</DataNote>}
            {p.blocks.map((b) => <Draw key={b} {...props} p={p} block={b} />)}
        </div>
    );
}

/** A ressalva da visão, acima dos blocos: o que estes números somam e o que eles não dizem. */
export const DataNote = ({ children }: { children: ReactNode }) => (
    <p style={{
        margin: 0, padding: '10px 14px', borderRadius: 12, fontSize: 11.5, lineHeight: 1.55,
        border: `1px solid ${v('border-soft')}`, background: v('inset'), color: v('muted'),
    }}>
        {children}
    </p>
);

/**
 * Um bloco. O funil não aparece na lista de casos porque ele não existe aqui: sem contador de
 * envio, quatro etapas de zero diriam que não se enviou nada.
 */
function Draw<R>({ block, p, preview, ...props }: DataBlocksProps<R> & {
    block: ScreenBlock;
    p: ScreenPrefs;
    /** No painel do Personalizar: a lista não se repete e os botões não navegam. */
    preview?: boolean;
}) {
    if (block === 'list') return preview ? null : <>{props.children}</>;
    if (!dataBlocks(props.kpis, props.measures).includes(block)) return null;
    if (!preview && p.hidden.includes(block)) return null;
    if (!props.items.length) return null;
    if (block === 'kpis') return <DataKpis {...props} p={p} preview={preview} />;
    if (block === 'chart') return <DataChart {...props} p={p} />;
    return null;
}

/* ----------------------------------------------------------------- indicadores */

function DataKpis<R>({ kpis, what, items, p, preview }: DataBlocksProps<R> & { p: ScreenPrefs; preview?: boolean }) {
    const { setDrawer } = useEmail();
    return (
        <KpiRow
            items={kpis ?? []}
            style={p.kpiStyle}
            sub={`${items.length} ${items.length === 1 ? what.replace(/s$/, '') : what} nesta lista`}
            markTitle="Recalculada a partir dos contadores somados desta lista"
            action="Ajustar indicadores"
            onCustomize={preview ? () => undefined : () => setDrawer('kpis')}
        />
    );
}

/* ----------------------------------------------------------------- gráfico */

function DataChart<R>({ screenKey, items, what, measures, p }: DataBlocksProps<R> & { p: ScreenPrefs }) {
    const { setScreenPrefs, setDrawer } = useEmail();
    const models = dataModels(measures, items.length);
    const model = pickModel(models, p.chart);
    const spec = models.find((m) => m.k === model) ?? models[0];

    return (
        <Block
            title={spec.l}
            sub={spec.d}
            right={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <ModelPicker opts={models} value={model} onChange={(chart) => setScreenPrefs(screenKey, { chart })} />
                    <GhostBtn onClick={() => setDrawer('chart')}>
                        <Settings2 size={14} color={v('muted')} />Gráfico
                    </GhostBtn>
                </div>
            )}
        >
            {model === 'mix' ? <Mix items={items} measures={measures} />
                : model === 'conc' ? <Conc items={items} measures={measures} what={what} />
                    : model === 'pair' ? <Pair items={items} measures={measures} what={what} />
                        : model === 'heat' ? <Heat items={items} measures={measures} what={what} />
                            : <Rank items={items} measures={measures} what={what} />}
        </Block>
    );
}

/** A primeira medida que soma — o padrão de todo modelo que precisa de total. */
const firstAdditive = <R,>(measures: Measure<R>[]) => (measures.find((m) => m.additive) ?? measures[0])?.k ?? '';

/** O ranking das linhas na medida escolhida. */
function Rank<R>({ items, measures, what }: { items: DataRow<R>[]; measures: Measure<R>[]; what: string }) {
    const [key, setKey] = useState(() => firstAdditive(measures));
    const m = measures.find((x) => x.k === key) ?? measures[0];
    const bars = useMemo<Bar[]>(() => items
        .map((it) => ({ it, n: m.get(it.row) }))
        // ausente vai para o fim nos dois sentidos: "—" não é zero, é outra coisa
        .sort((a, b) => (a.n == null ? 1 : b.n == null ? -1 : b.n - a.n))
        .slice(0, TOP_N)
        .map(({ it, n }) => ({ key: it.key, label: it.label, n, text: m.text(n), hue: it.hue ?? m.hue ?? 'blue' })),
        [items, m]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Pills opts={measures} value={m.k} onChange={setKey} />
            <RankBars bars={bars} count={items.length} what={what} />
        </div>
    );
}

/** A fatia de cada linha no total. Só as medidas que somam entram. */
function Mix<R>({ items, measures }: { items: DataRow<R>[]; measures: Measure<R>[] }) {
    const adds = measures.filter((x) => x.additive);
    const [key, setKey] = useState(() => adds[0]?.k ?? '');
    const m = adds.find((x) => x.k === key) ?? adds[0];
    const { slices, total } = useMemo(
        () => sliceUp(items.map((it) => ({
            key: it.key, label: it.label, hue: it.hue, n: Math.max(0, m.get(it.row) ?? 0),
        }))),
        [items, m],
    );
    const pills = <Pills opts={adds} value={m.k} onChange={setKey} />;
    const pct = (n: number) => `${n.toFixed(n < 10 ? 1 : 0).replace('.', ',')}%`;

    if (total <= 0) {
        return (
            <>
                {pills}
                <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                    Nenhuma linha tem {m.l.toLowerCase()} nesta janela — {DASH}, não zero: não há o que repartir.
                </p>
            </>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pills}
            <MixBar slices={slices} total={total} pct={pct} text={(n) => m.text(n)} />
        </div>
    );
}

/* ----------------------------------------------------------------- concentração */

const CUTS = [1, 3, 5, 10];

/**
 * Quanto do total está nas maiores linhas.
 *
 * É a pergunta que um ranking não responde: se três links fazem 80% dos cliques, o resto é cauda, e
 * mexer no quarto não muda nada. Os cortes maiores que a lista não aparecem — "os 10 maiores de 4
 * linhas" seria 100% sem querer dizer nada.
 */
function Conc<R>({ items, measures, what }: { items: DataRow<R>[]; measures: Measure<R>[]; what: string }) {
    const adds = measures.filter((x) => x.additive);
    const [key, setKey] = useState(() => adds[0]?.k ?? '');
    const m = adds.find((x) => x.k === key) ?? adds[0];
    const model = useMemo(() => {
        const ns = items.map((it) => Math.max(0, m.get(it.row) ?? 0)).sort((a, b) => b - a);
        const total = ns.reduce((a, x) => a + x, 0);
        const cuts = CUTS.filter((n) => n < ns.length);
        const bars: Bar[] = cuts.map((n) => {
            const sum = ns.slice(0, n).reduce((a, x) => a + x, 0);
            const share = total > 0 ? (sum / total) * 100 : 0;
            return {
                key: String(n),
                label: `top ${n}`,
                n: share,
                text: `${share.toFixed(share < 10 ? 1 : 0).replace('.', ',')}% · ${m.text(sum)}`,
                hue: m.hue ?? 'blue',
            };
        });
        return { bars, total, count: ns.length };
    }, [items, m, what]);
    const pills = <Pills opts={adds} value={m.k} onChange={setKey} />;

    if (model.total <= 0 || !model.bars.length) {
        return (
            <>
                {pills}
                <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                    {model.total <= 0
                        ? <>Nenhuma linha tem {m.l.toLowerCase()} nesta janela — não há total para repartir.</>
                        : <>A lista tem {model.count} {model.count === 1 ? what.replace(/s$/, '') : what}: com tão poucas
                            linhas, todo corte daria 100% e não diria nada.</>}
                </p>
            </>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pills}
            <RankBars bars={model.bars} count={model.bars.length} what={what} />
            <span style={{ fontSize: 11, color: v('faint') }}>
                a fatia das maiores no total de {m.text(model.total)} desta lista, somando {model.count} {what}
            </span>
        </div>
    );
}

/* ----------------------------------------------------------------- painel do Personalizar */

/**
 * O mesmo desenho, para o painel da direita do Personalizar. Bloco desligado aparece mesmo assim,
 * com o aviso — senão a aba em que se está mexendo ficaria em branco, sem dizer por quê.
 */
export function DataBlocksPreview({ screenKey, reg, only }: {
    screenKey: ScreenKey;
    reg: DataReg;
    only: ScreenBlock | 'all';
}) {
    const { prefs } = useEmail();
    const p = prefs.screens[screenKey];
    const off = only !== 'all' && p.hidden.includes(only);
    const drawn = only === 'all'
        ? p.blocks.filter((b) => b !== 'list' && reg.blocks.includes(b) && !p.hidden.includes(b))
        : [only];

    if (!reg.count) {
        return (
            <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                A lista desta tela não tem linha no período — os blocos não têm o que somar. Os ajustes ficam
                salvos do mesmo jeito.
            </p>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {off && (
                <DataNote>
                    {SCREEN_BLOCK_META[only].l} está desligado nesta tela — aqui ele aparece só para você ver o que
                    está ajustando.
                </DataNote>
            )}
            {reg.note && <DataNote>{reg.note}</DataNote>}
            {drawn.map((b) => <div key={b}>{reg.block(b, p)}</div>)}
            {drawn.some((b) => !reg.blocks.includes(b)) && (
                <p style={{ margin: 0, fontSize: 11.5, color: v('faint'), lineHeight: 1.5 }}>
                    Esta visão não tem este bloco: ela conta cliques e pessoas, não mensagens enviadas — não há
                    etapa de entrega para um funil, nem indicador de envio para somar.
                </p>
            )}
            {only === 'all' && (
                <p style={{ margin: 0, fontSize: 11.5, color: v('faint'), lineHeight: 1.5 }}>
                    A lista vem depois destes blocos — ela não sai da tela, e por isso não tem chave para desligar.
                </p>
            )}
        </div>
    );
}

/* ----------------------------------------------------------------- medida × medida */

/** Escolha de um eixo: os mesmos botões do ranking, com o nome do eixo na frente. */
function AxisRow<R>({ l, opts, value, onChange }: { l: string; opts: Measure<R>[]; value: string; onChange: (k: string) => void }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: v('faint'), width: 48, flexShrink: 0 }}>
                {l}
            </span>
            <Pills opts={opts} value={value} onChange={onChange} />
        </div>
    );
}

/**
 * Duas medidas à escolha, uma bolha por linha. O tamanho é a primeira medida que SOMA — em Cliques,
 * os cliques: assim a bolha grande é o link que mais pesa, e não um link de dois cliques com uma
 * razão alta.
 *
 * Linha sem uma das duas não entra: um ponto em (0,0) diria "deu zero", e ausência não é zero.
 */
function Pair<R>({ items, measures, what }: { items: DataRow<R>[]; measures: Measure<R>[]; what: string }) {
    const [xk, setXk] = useState(() => measures[0]?.k ?? '');
    const [yk, setYk] = useState(() => measures[1]?.k ?? measures[0]?.k ?? '');
    const mx = measures.find((m) => m.k === xk) ?? measures[0];
    const my = measures.find((m) => m.k === yk) ?? measures[1] ?? measures[0];
    const size = measures.find((m) => m.additive) ?? measures[0];
    const dots = useMemo<Dot[]>(() => items.flatMap((it) => {
        const x = mx.get(it.row);
        const y = my.get(it.row);
        if (x == null || y == null) return [];
        return [{ key: it.key, label: it.label, hue: it.hue, x, y, size: size.get(it.row) ?? 0 }];
    }), [items, mx, my, size]);

    const tick = (m: Measure<R>) => (n: number) => m.text(n);
    const axes = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <AxisRow l="eixo x" opts={measures} value={mx.k} onChange={setXk} />
            <AxisRow l="eixo y" opts={measures} value={my.k} onChange={setYk} />
        </div>
    );

    if (!dots.length) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {axes}
                <p style={{ margin: 0, fontSize: 12.5, color: v('faint'), lineHeight: 1.5 }}>
                    Nenhuma linha tem {mx.l.toLowerCase()} e {my.l.toLowerCase()} nesta janela — as duas são {DASH} e
                    não há ponto para marcar.
                </p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {axes}
            <ScatterPlot
                dots={dots}
                xTick={tick(mx)}
                yTick={tick(my)}
                tip={(d) => [
                    { l: mx.l, val: mx.text(d.x), col: c(mx.hue ?? 'violet') },
                    { l: my.l, val: my.text(d.y), col: c(my.hue ?? 'pink') },
                    { l: size.l, val: size.text(d.size), col: 'transparent' },
                ]}
                footer={(
                    <>
                        <span>eixo x: {mx.l.toLowerCase()} · eixo y: {my.l.toLowerCase()} · tamanho: {size.l.toLowerCase()}</span>
                        {dots.length < items.length && (
                            <span>{items.length - dots.length} de {items.length} {what} ficaram de fora por não ter uma das duas</span>
                        )}
                    </>
                )}
            />
        </div>
    );
}

/* ----------------------------------------------------------------- mapa de calor */

const HEAT_N = 12;

/** Medidas × linhas, contra a média da lista. A grade é a de `chartbits`; aqui só o catálogo. */
function Heat<R>({ items, measures, what }: { items: DataRow<R>[]; measures: Measure<R>[]; what: string }) {
    const first = measures.find((m) => m.additive) ?? measures[0];
    const shown = useMemo(
        () => [...items].sort((a, b) => (first.get(b.row) ?? 0) - (first.get(a.row) ?? 0)).slice(0, HEAT_N),
        [items, first],
    );
    const byKey = useMemo(() => new Map(shown.map((r) => [r.key, r])), [shown]);
    const byCol = useMemo(() => new Map(measures.map((m) => [m.k, m])), [measures]);
    const cols: HeatCol[] = measures.map((m) => ({ k: m.k, l: m.l, d: m.d }));
    if (!shown.length) return null;

    return (
        <HeatGrid
            rows={shown.map((r) => ({ key: r.key, label: r.label }))}
            cols={cols}
            get={(r, col) => {
                const it = byKey.get(r.key);
                const m = byCol.get(col.k);
                return it && m ? m.get(it.row) : null;
            }}
            text={(col, n) => byCol.get(col.k)?.text(n) ?? DASH}
            note={(
                <>
                    A cor é a distância da média desta lista, não de uma meta: verde é acima das outras linhas que
                    você está vendo, não “bom”. Célula cinza com {DASH} é número que não existe nessa linha, e não
                    zero.
                    {items.length > HEAT_N && <> As {HEAT_N} maiores em {first.l.toLowerCase()}, de {items.length} {what} — a lista inteira fica abaixo.</>}
                </>
            )}
        />
    );
}
